import React from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowRight, Megaphone } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import i18n from '@/utils/i18n';
import { Routes } from '../../utils/routes';

type TryNewDesignBannerProps = {
  /** Resolved strings — default to the Container Types (EditContainerInfo) copy. */
  title?: string;
  text?: string;
  cta?: string;
  /** Route the CTA forwards to — defaults to the Container Types redesign. */
  targetRoute?: string;
};

/**
 * Shown on legacy admin screens every time they open: points to a redesigned
 * page. Deliberately shadcn/tw-styled and warning-tinted so it cannot be missed
 * on the Bootstrap host pages — the legacy pages retire once the redesign
 * sticks. Defaults to the Container Types redesign, but title/text/cta/target
 * can be overridden to reuse it for any redesign (e.g. Container type groups).
 */
export default function TryNewDesignBanner({
  title,
  text,
  cta,
  targetRoute,
}: TryNewDesignBannerProps = {}) {
  const history = useHistory();

  return (
    <Alert
      className="tw:mb-4 tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:border-l-4 tw:py-3.5 tw:shadow-sm"
      style={{
        backgroundColor:
          'color-mix(in oklab, var(--color-warning-main) 14%, var(--background))',
        borderColor:
          'color-mix(in oklab, var(--color-warning-main) 45%, transparent)',
        borderLeftColor: 'var(--color-warning-main)',
      }}
    >
      <span
        className="tw:flex tw:size-9 tw:flex-none tw:items-center tw:justify-center tw:rounded-full"
        style={{
          backgroundColor:
            'color-mix(in oklab, var(--color-warning-main) 28%, var(--background))',
          color: '#b45309',
        }}
      >
        <Megaphone size={18} />
      </span>
      <div className="tw:min-w-0 tw:flex-1">
        <AlertTitle className="tw:text-[15px] tw:font-semibold tw:text-foreground">
          {title ?? i18n.t('EditContainerInfo:newDesignBannerTitle')}
        </AlertTitle>
        <AlertDescription className="tw:text-foreground/80">
          {text ?? i18n.t('EditContainerInfo:newDesignBannerText')}
        </AlertDescription>
      </div>
      <Button
        className="tw:flex-none"
        onClick={() => history.push(targetRoute ?? Routes.editContainerTypes())}
      >
        {cta ?? i18n.t('EditContainerInfo:tryNewDesign')}
        <ArrowRight size={15} />
      </Button>
    </Alert>
  );
}
